import {
  ICustomProductOptionConfig,
  ICustomProductOptionConfigItem,
  ICustomProductOptionItem,
  IDraftCustomProductOptionConfigItem,
} from "@evennt-tech/api-v2-core";
import {
  IGetAllCustomProductOptionsSupplierBodyRes,
  IGetProductColorsOptionsBodyRes,
} from "@evennt-tech/api-v2-external";
import {
  ColorSetting,
  FormCustomProductOptionConfig,
  FormCustomProductOptionConfigItem,
} from "view/apps/Supplier/pages/Products/List/components/ProductDetailsDrawer/components/CustomProductOptionConfigModal/types";
import { v4 as uuid } from "uuid";
import { OrderProductCpoConfig } from "ts/api/order";

export const getCpoItemById = (
  cpoItems: ICustomProductOptionItem[],
  cpoItemId: string
) => cpoItems.find((item) => item.id === cpoItemId);

export const getCpoById = (
  customOptions: IGetAllCustomProductOptionsSupplierBodyRes[],
  cpoId: string
) => customOptions.find((cpo) => cpo.id === cpoId) ?? null;

export const getConfigItemsFromColorSetting = (
  colorSetting: ColorSetting,
  { id: colorCpoId }: IGetProductColorsOptionsBodyRes
): FormCustomProductOptionConfigItem[] => {
  const result: FormCustomProductOptionConfigItem[] = [];
  const { firstColor, secondColor } = colorSetting;
  if (!firstColor) {
    return result;
  }
  result.push({
    cpoItemId: firstColor,
    cpoId: colorCpoId,
    id: uuid(),
    isDraft: true,
  });
  if (secondColor) {
    result.push({
      cpoItemId: secondColor,
      cpoId: colorCpoId,
      id: uuid(),
      isDraft: true,
    });
  }

  return result;
};
export const getColorSettingFromCpoConfig = (
  colorCpo: IGetProductColorsOptionsBodyRes,
  { items = [] }: FormCustomProductOptionConfig
): ColorSetting | null => {
  if (!items?.length) {
    return null;
  }
  const [firstColor, secondColor] = items.filter(
    (item) => item.cpoId === colorCpo.id
  );
  if (!firstColor) {
    return null;
  }
  return {
    firstColor: firstColor.cpoItemId,
    secondColor: secondColor?.cpoItemId ?? "",
  };
};

type SeparatedCpoConfigItems<T> = {
  colorItems: T[];
  regularItems: T[];
};

export const separateCpoConfigItemsWithColor = (
  configItems: FormCustomProductOptionConfigItem[],
  colorCpoId: string
): SeparatedCpoConfigItems<FormCustomProductOptionConfigItem> => {
  return configItems.reduce<
    SeparatedCpoConfigItems<FormCustomProductOptionConfigItem>
  >(
    (acc, curr) => {
      if (curr.cpoId === colorCpoId) {
        acc.colorItems.push(curr);
      } else {
        acc.regularItems.push(curr);
      }

      return acc;
    },
    { colorItems: [], regularItems: [] }
  );
};

export const getConfigurationsCountByCpo = (
  colorCpo: IGetProductColorsOptionsBodyRes,
  customOptions: IGetAllCustomProductOptionsSupplierBodyRes[],
  configs: FormCustomProductOptionConfig[]
) => {
  // Name lookups built once (O(total CPO items)) instead of a `.find()`
  // per config-item nested three loops deep (dimensions × configs ×
  // items-per-config) — with ~300 items across many saved configs that
  // nested-scan pattern was the dominant cost of opening the Product
  // drawer.
  const itemNameByCpoIdAndItemId = new Map<string, Map<string, string>>();
  const titleByCpoId = new Map<string, string>();
  customOptions.forEach(({ id, title, cpoItems }) => {
    titleByCpoId.set(id, title);
    itemNameByCpoIdAndItemId.set(
      id,
      new Map(cpoItems.map((item) => [item.id, item.name]))
    );
  });
  const colorItemNameById = new Map(
    colorCpo.cpoItems.map((item) => [item.id, item.name])
  );

  const configsByTitle: Record<string, string[]> = {};
  const colorConfigs: string[] = [];

  configs.forEach(({ items = [], enabled }) => {
    if (!enabled) {
      return;
    }

    const namesByTitle = new Map<string, string[]>();
    let firstColorName: string | undefined;
    let secondColorName: string | undefined;

    items.forEach(({ cpoId, cpoItemId }) => {
      if (cpoId === colorCpo.id) {
        const colorName = colorItemNameById.get(cpoItemId);
        if (colorName) {
          if (!firstColorName) {
            firstColorName = colorName;
          } else if (!secondColorName) {
            secondColorName = colorName;
          }
        }
        return;
      }

      const title = titleByCpoId.get(cpoId);
      const name = itemNameByCpoIdAndItemId.get(cpoId)?.get(cpoItemId);
      if (!title || !name) {
        return;
      }
      const names = namesByTitle.get(title) ?? [];
      names.push(name);
      namesByTitle.set(title, names);
    });

    namesByTitle.forEach((names, title) => {
      if (!configsByTitle[title]) {
        configsByTitle[title] = [];
      }
      configsByTitle[title].push(...names);
    });

    if (firstColorName) {
      colorConfigs.push(
        `${firstColorName}${secondColorName ? `-${secondColorName}` : ""}`
      );
    }
  });

  if (colorConfigs.length) {
    configsByTitle[colorCpo.title] = colorConfigs;
  }

  return configsByTitle;
};

export const getOptionItemNameFromConfigItem = (
  configItem: IDraftCustomProductOptionConfigItem,
  colorItems: ICustomProductOptionItem[] = []
) => {
  return colorItems.find(({ id }) => id === configItem?.cpoItemId)?.name ?? "";
};

export const setValueAsNumberOrNull = (value: any) =>
  ["", null].includes(value) ? null : Number(value);

export const shouldRemoveConfigItemRemote = (
  cpoConfig: FormCustomProductOptionConfig,
  itemIdToRemove: string
) =>
  cpoConfig.items?.some((item) => item.id === itemIdToRemove && !item.isDraft);

export const separateConfigItemsByType = (
  items: ICustomProductOptionConfigItem[] = []
) =>
  items.reduce<SeparatedCpoConfigItems<ICustomProductOptionConfigItem>>(
    (acc, curr) => {
      if (!curr.type) {
        acc.colorItems.push(curr);
      } else {
        acc.regularItems.push(curr);
      }

      return acc;
    },
    { colorItems: [], regularItems: [] }
  );

export const getConcatCpoColorValue = (
  colorItems: ICustomProductOptionConfigItem[]
) => {
  const [firstColorItem, secondColorItem] = colorItems;
  const firstColorValue = firstColorItem?.cpoItem?.optionId;
  if (!firstColorValue) {
    return "";
  }

  return `${firstColorItem.cpoItem?.name}${
    secondColorItem ? `-${secondColorItem?.cpoItem?.name}` : ""
  }`;
};

export const getCpoConfigItemsConcatValue = (
  groupedConfigItems: SeparatedCpoConfigItems<ICustomProductOptionConfigItem>,
  separator = "; "
) => {
  const { colorItems, regularItems } = groupedConfigItems;
  const baseColorValue = getConcatCpoColorValue(colorItems);

  const colorValue = `${baseColorValue}${
    regularItems.length && baseColorValue ? separator : ""
  }`;
  const regularValue = regularItems
    .map((configItem) => configItem.cpoItem?.name ?? "")
    .join(separator);

  return `${colorValue}${regularValue}`;
};

export const extractOrderProductConfigItemsFromFullConfig = (
  fullConfig: ICustomProductOptionConfig,
  productConfig: OrderProductCpoConfig
) => {
  return productConfig.items.reduce<ICustomProductOptionConfigItem[]>(
    (acc, curr) => {
      const existingItem = fullConfig.items?.find((i) => i.id === curr.id);
      if (existingItem) {
        acc.push(existingItem);
      }

      return acc;
    },
    []
  );
};
